import React, { useState, useEffect } from 'react';
import { Tag, Plus, X } from 'lucide-react';
import AmministartionTagService from '../../services/amministrationtag.service';
import PropTypes from 'prop-types';

const AmministrationTagManagment = ({ therapistId, initialTags = [] }) => {
    const [availableTags, setAvailableTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState(initialTags);
    const [isLoading, setIsLoading] = useState(true);

    // Carica tutti i tag disponibili e quelli assegnati all'avvio
    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true);
                
                // Carica tutti i tag disponibili
                const tagsResponse = await AmministartionTagService.getAllTags();
                const allTags = tagsResponse.success && Array.isArray(tagsResponse.data) 
                    ? tagsResponse.data 
                    : [];
                
                setAvailableTags(allTags);
                
                // Carica i tag assegnati al terapeuta
                const therapistTagsResponse = await AmministartionTagService.getTherapistTags(therapistId);
                if (therapistTagsResponse.success && Array.isArray(therapistTagsResponse.data)) {
                    setSelectedTags(therapistTagsResponse.data);
                }
                
                console.log('Data loaded successfully:', { 
                    availableTags: allTags, 
                    selectedTags: therapistTagsResponse.data 
                });
            } catch (error) {
                console.error('[DEBUG] Error loading tags:', error);
                setAvailableTags([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [therapistId]);

    // Aggiorna i tag selezionati quando cambiano gli initialTags
    useEffect(() => {
        if (initialTags.length > 0) {
            setSelectedTags(initialTags);
        }
    }, [initialTags]);

    const handleAddTag = async (tag) => {
        try {
            // Usiamo AmministartionTagService
            await AmministartionTagService.assignTagToTherapist(therapistId, tag.id);
            setSelectedTags(prev => [...prev, tag]);
        } catch (error) {
            console.error('Errore nell\'aggiunta del tag:', error);
        }
    };

    const handleRemoveTag = async (tag) => {
        try {
            // Usiamo AmministartionTagService
            await AmministartionTagService.removeTagFromTherapist(therapistId, tag.id);
            setSelectedTags(prev => prev.filter(t => t.id !== tag.id));
        } catch (error) {
            console.error('[DEBUG] Error removing tag:', error);
        }
    };

    // Filtra i tag disponibili escludendo quelli già selezionati
    const getAvailableTagsForSelection = () => {
        if (!Array.isArray(availableTags)) return [];
        
        return availableTags.filter(tag => 
            !selectedTags.some(selectedTag => selectedTag.id === tag.id)
        );
    };

    // Mostra un loader durante il caricamento
    if (isLoading) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <div className="animate-pulse flex space-x-4">
                    <div className="flex-1 space-y-4 py-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="space-y-2">
                            <div className="h-4 bg-gray-200 rounded"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white p-6 rounded-lg  mb-6">
            <div className="flex items-center mb-4">
                <Tag className="w-5 h-5 text-purple-600 mr-2" />
                <h2 className="text-lg font-semibold">Tag Amministrativi del professionista</h2>
            </div>

            {/* Sezione dei tag selezionati */}
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tag assegnati
                </label>
                <div className="flex flex-wrap gap-2">
                {selectedTags.map((tag, index) => (
                        <span
                        key={`${tag.id}-${index}`}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-700"
                        >
                            {tag.name}
                            <button
                                onClick={() => handleRemoveTag(tag)}
                                className="ml-2 hover:text-purple-900"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </span>
                    ))}
                    {selectedTags.length === 0 && (
                        <span className="text-gray-500 text-sm">
                            Nessun tag assegnato
                        </span>
                    )}
                </div>
            </div>

            {/* Sezione dei tag disponibili */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tag disponibili
                </label>
                <div className="flex flex-wrap gap-2">
                {getAvailableTagsForSelection().map((tag, index) => (
                        <button
                        key={`available-${tag.id}-${index}`}
                        onClick={() => handleAddTag(tag)}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm border border-purple-300 text-purple-600 hover:bg-purple-50"
                        >
                            <Plus className="w-4 h-4 mr-1" />
                            {tag.name}
                        </button>
                    ))}
                    {getAvailableTagsForSelection().length === 0 && (
                        <span className="text-gray-500 text-sm">
                            Nessun tag disponibile
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

AmministrationTagManagment.propTypes = {
    therapistId: PropTypes.number.isRequired,
    initialTags: PropTypes.array
};

export default AmministrationTagManagment;